import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaTshirt, FaIndustry, FaShippingFast, FaUsers, FaCheckCircle, FaStar, FaArrowRight, FaQuoteLeft, FaAward, FaCertificate, FaHandshake, FaChartLine } from 'react-icons/fa';
import { MdHighQuality } from 'react-icons/md';
import productsData from '../data/products.json';
// Dynamic product images (same approach as Products/Product pages)
const productImages = require.context('../assets/Products', false, /\.(png|jpe?g|webp|svg)$/);
function getImageSrc(filename) {
    if (!filename) return '';
    try {
        return productImages(`./${filename}`);
    } catch (e) {
        return '';
    }
}

function Home() {
    const [isVisible, setIsVisible] = useState({});
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const observerRefs = useRef([]);

    const featuredProducts = productsData.filter(p => p.isFeatured).slice(0, 8);
    const categories = [
        { name: 'Men', slug: 'men', desc: 'Premium quality men\'s wear', icon: '👔', count: productsData.filter(p => p.category === 'men').length },
        { name: 'Women', slug: 'women', desc: 'Stylish women\'s collection', icon: '👗', count: productsData.filter(p => p.category === 'women').length },
        { name: 'Kids', slug: 'kids', desc: 'Comfortable kids apparel', icon: '👶', count: productsData.filter(p => p.category === 'kids').length }
    ];

    const testimonials = [
        { name: 'Rajesh Kumar', location: 'Mumbai', rating: 5, text: 'Outstanding quality and unbeatable wholesale prices. SS Tex has been our trusted partner for 3 years. Their delivery is always on time.' },
        { name: 'Priya Sharma', location: 'Delhi', rating: 5, text: 'The feeding kurtis and maternity wear are exceptional. Soft fabrics and perfect stitching. Highly recommend for bulk orders!' },
        { name: 'Amit Patel', location: 'Ahmedabad', rating: 5, text: 'Factory-direct pricing with premium quality. We order kids wear regularly and customers love the durability and designs.' }
    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.dataset.section]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        observerRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [testimonials.length]);

    return (
        <div className="bg-white">
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse delay-700" />
                    <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000" />
                </div>

                <div className="max-w-7xl mx-auto px-4 py-20 md:py-32 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8 animate-fade-in">
                            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                                <FaAward className="text-yellow-300" />
                                <span className="text-sm font-semibold">7+ Years of Excellence</span>
                            </div>
                            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                                Tiruppur's Trusted
                                <span className="block text-yellow-300 mt-2">Garment Manufacturer</span>
                            </h1>
                            <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-xl">
                                Premium quality apparel for men, women, and kids. Factory-direct pricing serving 2,000+ wholesale clients and 50,000+ retail customers across India.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/products" className="px-8 py-4 bg-white text-primary-600 rounded-xl font-bold text-lg hover:bg-yellow-300 hover:text-primary-700 transition transform hover:scale-105 shadow-2xl">
                                    Browse Products <FaArrowRight className="inline ml-2" />
                                </Link>
                                <Link to="/about" className="px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl font-bold text-lg hover:bg-white/20 transition transform hover:scale-105">
                                    Learn More
                                </Link>
                            </div>
                            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
                                <div>
                                    <div className="text-3xl md:text-4xl font-bold text-yellow-300">2,000+</div>
                                    <div className="text-sm text-white/80 mt-1">Wholesale Clients</div>
                                </div>
                                <div>
                                    <div className="text-3xl md:text-4xl font-bold text-yellow-300">50K+</div>
                                    <div className="text-sm text-white/80 mt-1">Retail Customers</div>
                                </div>
                                <div>
                                    <div className="text-3xl md:text-4xl font-bold text-yellow-300">7+</div>
                                    <div className="text-sm text-white/80 mt-1">Years Experience</div>
                                </div>
                            </div>
                        </div>
                        <div className="relative hidden lg:block">
                            <div className="relative z-10 transform hover:scale-105 transition duration-700">
                                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-2xl">
                                    <FaTshirt className="text-9xl text-white/80 mx-auto" />
                                </div>
                            </div>
                            <div className="absolute -top-10 -right-10 w-64 h-64 bg-yellow-300/20 rounded-full blur-3xl" />
                            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-300/20 rounded-full blur-3xl" />
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
                    </svg>
                </div>
            </section>

            {/* Categories Section */}
            <section ref={(el) => (observerRefs.current[0] = el)} data-section="categories" className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.categories ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Shop by Category</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">Explore our diverse range of premium garments across all categories</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {categories.map((cat, idx) => (
                            <Link
                                key={cat.slug}
                                to={`/products?category=${cat.slug}`}
                                className={`group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2 ${isVisible.categories ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${idx * 100}ms` }}
                            >
                                <div className="relative h-64 bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center overflow-hidden">
                                    <div className="text-9xl transform group-hover:scale-110 transition duration-500">{cat.icon}</div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                    <div className="absolute bottom-6 left-6 right-6">
                                        <div className="text-white text-3xl font-bold mb-1">{cat.name}</div>
                                        <div className="text-white/90 text-sm">{cat.count} Products</div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-gray-600 mb-4">{cat.desc}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-primary-600 font-semibold group-hover:text-primary-700">Shop Now</span>
                                        <FaArrowRight className="text-primary-600 transform group-hover:translate-x-2 transition" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section ref={(el) => (observerRefs.current[1] = el)} data-section="features" className="py-20 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Why Choose SS Tex?</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">Experience the difference with our commitment to quality and service</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            { icon: <FaIndustry />, title: 'Direct Factory', desc: 'No middlemen, best market price guaranteed' },
                            { icon: <MdHighQuality />, title: 'Premium Quality', desc: 'Multi-stage inspection for every product' },
                            { icon: <FaShippingFast />, title: 'All-India Delivery', desc: 'Fast logistics network nationwide' },
                            { icon: <FaHandshake />, title: 'OEM Services', desc: 'Private label & custom manufacturing' }
                        ].map((feature, idx) => (
                            <div
                                key={idx}
                                className={`bg-gradient-to-br from-primary-50 to-white rounded-2xl p-8 text-center shadow-md hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 ${isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${idx * 100}ms` }}
                            >
                                <div className="text-5xl text-primary-600 mb-4 flex justify-center">{feature.icon}</div>
                                <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                                <p className="text-gray-600">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section ref={(el) => (observerRefs.current[2] = el)} data-section="products" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.products ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Featured Products</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">Handpicked bestsellers from our premium collection</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {featuredProducts.map((product, idx) => {
                            const discount = product.offerPrice && product.offerPrice < product.actualPrice
                                ? Math.round(((product.actualPrice - product.offerPrice) / product.actualPrice) * 100)
                                : 0;
                            return (
                                <Link
                                    key={product.productId}
                                    to={`/product/${product.productId}`}
                                    className={`group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2 ${isVisible.products ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                    style={{ transitionDelay: `${idx * 50}ms` }}
                                >
                                    <div className="relative h-56 overflow-hidden">
                                        <img src={getImageSrc(product.images[0])} alt={product.title} className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700" />
                                        {discount > 0 && (
                                            <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">-{discount}%</span>
                                        )}
                                        {product.badge && (
                                            <span className="absolute top-3 right-3 bg-primary-600 text-white text-xs font-bold px-2 py-1 rounded-full">{product.badge}</span>
                                        )}
                                    </div>
                                    <div className="p-5">
                                        <h3 className="font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-primary-600 transition">{product.title}</h3>
                                        <div className="flex items-center gap-1 mb-3">
                                            {[...Array(5)].map((_, i) => (
                                                <FaStar key={i} className={`text-sm ${i < product.rating ? 'text-yellow-400' : 'text-gray-300'}`} />
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {product.offerPrice && product.offerPrice < product.actualPrice ? (
                                                <>
                                                    <span className="text-xl font-bold text-primary-600">₹{product.offerPrice}</span>
                                                    <span className="text-sm line-through text-gray-400">₹{product.actualPrice}</span>
                                                </>
                                            ) : (
                                                <span className="text-xl font-bold text-gray-800">₹{product.actualPrice}</span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                    <div className="text-center mt-12">
                        <Link to="/products" className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white rounded-xl font-bold hover:bg-primary-700 transition transform hover:scale-105 shadow-lg">
                            View All Products <FaArrowRight />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section ref={(el) => (observerRefs.current[3] = el)} data-section="testimonials" className="py-20 px-4 bg-gradient-to-br from-primary-600 to-primary-800 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-700" />
                </div>
                <div className="max-w-5xl mx-auto relative z-10">
                    <div className={`text-center mb-16 transition-all duration-1000 ${isVisible.testimonials ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
                        <p className="text-lg text-white/90 max-w-2xl mx-auto">Trusted by thousands of businesses across India</p>
                    </div>
                    <div className="relative">
                        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
                            <FaQuoteLeft className="text-5xl text-yellow-300 mb-6" />
                            <p className="text-xl md:text-2xl leading-relaxed mb-8 text-white/95">{testimonials[currentTestimonial].text}</p>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-bold text-xl text-yellow-300">{testimonials[currentTestimonial].name}</div>
                                    <div className="text-white/80">{testimonials[currentTestimonial].location}</div>
                                </div>
                                <div className="flex gap-1">
                                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                                        <FaStar key={i} className="text-yellow-300 text-xl" />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-center gap-3 mt-8">
                            {testimonials.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentTestimonial(idx)}
                                    className={`w-3 h-3 rounded-full transition ${idx === currentTestimonial ? 'bg-yellow-300 w-8' : 'bg-white/40 hover:bg-white/60'}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section ref={(el) => (observerRefs.current[4] = el)} data-section="stats" className="py-20 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8">
                        {[
                            { icon: <FaUsers />, number: '2000+', label: 'Wholesale Clients' },
                            { icon: <FaCheckCircle />, number: '50K+', label: 'Happy Customers' },
                            { icon: <FaChartLine />, number: '7+', label: 'Years Experience' },
                            { icon: <FaCertificate />, number: '100%', label: 'Quality Assured' }
                        ].map((stat, idx) => (
                            <div
                                key={idx}
                                className={`text-center transform transition-all duration-1000 ${isVisible.stats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${idx * 100}ms` }}
                            >
                                <div className="text-5xl text-primary-600 mb-4 flex justify-center">{stat.icon}</div>
                                <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-2">{stat.number}</div>
                                <div className="text-gray-600 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 bg-gradient-to-r from-primary-600 to-primary-800 text-white">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Partner with Us?</h2>
                    <p className="text-xl mb-8 text-white/90 max-w-2xl mx-auto">
                        Join 2,000+ businesses who trust SS Tex for premium garments at factory-direct prices
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/products" className="px-8 py-4 bg-white text-primary-600 rounded-xl font-bold text-lg hover:bg-yellow-300 hover:text-primary-700 transition transform hover:scale-105 shadow-2xl">
                            Browse Catalog
                        </Link>
                        <Link to="/contact" className="px-8 py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl font-bold text-lg hover:bg-white/20 transition transform hover:scale-105">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Home;